import { TextField, Select, MenuItem, FormControl, Tooltip } from "@mui/material";
import { TimeField } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState, useContext } from "react";
import DateLocalizationProvider from "../../../../components/element/DateLocalizationProvider";
import SearchAircraftDialog from "../../../../components/element/SearchAircraftDialog";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { AirportType, DATE_FORMAT, TIME_FORMAT, REJECTION_REASONS_COMMERCIAL } from "../../../../utils/constants";
import { getAirportData, getFlightNumber, getFlightDelayTime } from "../../../../utils/utils";
import infoIcon from "../../../../assets/images/info_icon.svg";
import "../TripDetails/TripDetails.css";

dayjs.extend(utc);

const TripDetailsTable = ({
  isEditing,
  airportData,
  flightPlanDetail,
  editedFlightDetails,
  setEditedFlightDetails,
}) => {
  const { fetchAircraftDetails } = useAirlineAdminApi();
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [showSearchAircraftDialog, setShowSearchAircraftDialog] =
    useState(false);
  const [selectedDepartureTerminal, setSelectedDepartureTerminal] =
    useState("");
  const [selectedArrivalTerminal, setSelectedArrivalTerminal] = useState("");

  const { data: aircraftData } = useQuery({
    queryKey: ["get-aircraft-data", flightPlanDetail?.aircraftTypeId],
    queryFn: () => fetchAircraftDetails(flightPlanDetail?.aircraftTypeId),
    enabled: !!flightPlanDetail?.aircraftTypeId,
  });

  useEffect(() => {
    setSelectedAircraft(aircraftData);
  }, [aircraftData]);

  useEffect(() => {
    if (
      flightPlanDetail?.departureAirportId &&
      isDOMAirport(flightPlanDetail.departureAirportId)
    ) {
      if (flightPlanDetail?.departureAirportTerminalId) {
        setSelectedDepartureTerminal(
          flightPlanDetail.departureAirportTerminalId
        );
      }
    }

    if (
      flightPlanDetail?.arrivalAirportId &&
      isDOMAirport(flightPlanDetail.arrivalAirportId)
    ) {
      if (flightPlanDetail?.arrivalAirportTerminalId) {
        setSelectedArrivalTerminal(flightPlanDetail.arrivalAirportTerminalId);
      }
    }
  }, [flightPlanDetail, airportData]);

  const handleAircraftRegInputChange = (event) => {
    setEditedFlightDetails({
      ...editedFlightDetails,
      aircraftRegistrationNumber: event.target.value.toUpperCase(),
    });
  };

  const handleAircraftOnSelect = (aircraft) => {
    setSelectedAircraft(aircraft);
    setEditedFlightDetails({
      ...editedFlightDetails,
      aircraftTypeId: aircraft?.id,
    });
  };

  const handleDelayTimeChange = (key) => (newValue) => {
    if (newValue) {
      const formattedTime = dayjs(newValue).format(TIME_FORMAT);
      setEditedFlightDetails({
        ...editedFlightDetails,
        [key]: formattedTime,
      });
    }
  };


  // Helper function to get airport data by ID (using common utils)
  const getAirportById = (airportId) => getAirportData(airportData, airportId);

  // Helper function to check if airport is DOM type
  const isDOMAirport = (airportId) => {
    const airport = getAirportById(airportId);
    return airport?.airportType === AirportType.DOMESTIC;
  };

  // Helper function to get terminal name by ID
  const getTerminalName = (airportId, terminalId) => {
    const airport = getAirportById(airportId);
    if (!airport || !airport.terminals) return "";
    const terminal = airport.terminals.find((t) => t.id === terminalId);
    return terminal?.name || "";
  };

  // Helper function to get rejection reason message
  const getRejectionReasonMessage = (reason) => {
    return REJECTION_REASONS_COMMERCIAL[reason] || reason;
  };

  // Handle terminal selection for departure
  const handleDepartureTerminalChange = (event) => {
    const terminalId = event.target.value;
    setSelectedDepartureTerminal(terminalId);

    // Only add terminal ID to editedFlightDetails if a terminal is selected
    if (terminalId && terminalId !== "") {
      setEditedFlightDetails({
        ...editedFlightDetails,
        departureAirportTerminalId: terminalId,
      });
    } else {
      // Remove the key if no terminal is selected
      const { departureAirportTerminalId, ...rest } = editedFlightDetails;
      setEditedFlightDetails(rest);
    }
  };

  // Handle terminal selection for arrival
  const handleArrivalTerminalChange = (event) => {
    const terminalId = event.target.value;
    setSelectedArrivalTerminal(terminalId);

    // Only add terminal ID to editedFlightDetails if a terminal is selected
    if (terminalId && terminalId !== "") {
      setEditedFlightDetails({
        ...editedFlightDetails,
        arrivalAirportTerminalId: terminalId,
      });
    } else {
      // Remove the key if no terminal is selected
      const { arrivalAirportTerminalId, ...rest } = editedFlightDetails;
      setEditedFlightDetails(rest);
    }
  };

  return (
    <DateLocalizationProvider>
      <div className="view-historical-data-table-parent-container">
        <div className="view-historical-data-table-container">
          <table className="view-historical-data-table">
            <thead>
              <tr>
                <th className="width-90">Flight No.</th>
                <th className="width-90">Tail number</th>
                <th className="width-110">Aircraft Type</th>
                <th className="width-110">Engine Type</th>
                <th className="width-110">Engine Count</th>
                <th className="width-90">MTOW</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <p>{getFlightNumber(flightPlanDetail)}</p>
                </td>
                <td>
                  {isEditing ? (
                    <TextField
                      id="flight-plan-tail-no"
                      variant="outlined"
                      className="view-historical-data-textfield"
                      inputProps={{
                        autoComplete: "off",
                        maxLength: 15,
                      }}
                      style={{ width: "120px" }}
                      value={editedFlightDetails?.aircraftRegistrationNumber}
                      onChange={handleAircraftRegInputChange}
                    />
                  ) : (
                    <p>
                      {flightPlanDetail?.aircraftRegistrationNumber
                        ? flightPlanDetail.aircraftRegistrationNumber.toUpperCase()
                        : "---"}
                    </p>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <div
                      className="view-historical-data-select-aircraft"
                      onClick={() => setShowSearchAircraftDialog(true)}
                    >
                      <p>{selectedAircraft?.icaoCode}</p>
                    </div>
                  ) : (
                    <p>{aircraftData?.icaoCode ?? "---"}</p>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <p>{selectedAircraft?.engineType ?? "---"}</p>
                  ) : (
                    <p>{aircraftData?.engineType ?? "---"}</p>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <p>{selectedAircraft?.engineCount ?? "---"}</p>
                  ) : (
                    <p>{aircraftData?.engineCount ?? "---"}</p>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <p>{selectedAircraft?.maxTakeOffWeight ?? "---"}</p>
                  ) : (
                    <p>{aircraftData?.maxTakeOffWeight ?? "---"}</p>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="view-historical-data-table-container">
          <table className="view-historical-data-table">
            <thead>
              <tr>
                <th>Origin</th>
                <th>Destination</th>
                <th className="width-200">Depart Date and Time</th>
                <th className="width-200">Arrival Date and Time</th>
                <th className="width-300">
                  Departure From The Arrival Airport Date and Time
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="airport-terminal-container">
                    {isEditing &&
                      isDOMAirport(flightPlanDetail?.departureAirportId) ? (
                      <>
                        <p className="airport-name">
                          {
                            getAirportData(
                              airportData,
                              flightPlanDetail?.departureAirportId
                            )?.name
                          }
                        </p>
                        <FormControl
                          size="small"
                          className="terminal-dropdown-container"
                        >
                          <Select
                            value={selectedDepartureTerminal}
                            onChange={handleDepartureTerminalChange}
                            displayEmpty
                            variant="outlined"
                            className="terminal-dropdown"
                          >
                            <MenuItem value="">
                              <em>Select</em>
                            </MenuItem>
                            {getAirportById(
                              flightPlanDetail?.departureAirportId
                            )?.terminals?.map((terminal) => (
                              <MenuItem key={terminal.id} value={terminal.id}>
                                {terminal.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    ) : (
                      <p
                        className="airport-name"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {
                          getAirportData(
                            airportData,
                            flightPlanDetail?.departureAirportId
                          )?.name
                        }
                        {isDOMAirport(flightPlanDetail?.departureAirportId) && (
                          <span className="terminal-text">
                            {getTerminalName(
                              flightPlanDetail?.departureAirportId,
                              flightPlanDetail?.departureAirportTerminalId
                            ) || "Main Terminal"}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </td>
                <td>
                  <div className="airport-terminal-container">
                    {isEditing &&
                      isDOMAirport(flightPlanDetail?.arrivalAirportId) ? (
                      <>
                        <p className="airport-name">
                          {
                            getAirportData(
                              airportData,
                              flightPlanDetail?.arrivalAirportId
                            )?.name
                          }
                        </p>
                        <FormControl
                          size="small"
                          className="terminal-dropdown-container"
                        >
                          <Select
                            value={selectedArrivalTerminal}
                            onChange={handleArrivalTerminalChange}
                            displayEmpty
                            variant="outlined"
                            className="terminal-dropdown"
                          >
                            <MenuItem value="">
                              <em>Select</em>
                            </MenuItem>
                            {getAirportById(
                              flightPlanDetail?.arrivalAirportId
                            )?.terminals?.map((terminal) => (
                              <MenuItem key={terminal.id} value={terminal.id}>
                                {terminal.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </>
                    ) : (
                      <p
                        className="airport-name"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {
                          getAirportData(
                            airportData,
                            flightPlanDetail?.arrivalAirportId
                          )?.name
                        }
                        {isDOMAirport(flightPlanDetail?.arrivalAirportId) && (
                          <span className="terminal-text">
                            {getTerminalName(
                              flightPlanDetail?.arrivalAirportId,
                              flightPlanDetail?.arrivalAirportTerminalId
                            ) || "Main Terminal"}
                            {flightPlanDetail?.fboFlightRejectionReason && (
                              <Tooltip
                                title={getRejectionReasonMessage(
                                  flightPlanDetail?.fboFlightRejectionReason
                                )}
                                arrow
                              >
                                <img
                                  src={infoIcon}
                                  alt="info"
                                  className="trip-details-rejection-info-icon"
                                />
                              </Tooltip>
                            )}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </td>
                <td>
                  <div>
                    <p>
                      {`${dayjs(flightPlanDetail?.departureTime).format(
                        DATE_FORMAT
                      )} at ${dayjs(flightPlanDetail?.departureTime).format(
                        TIME_FORMAT
                      )}`}
                    </p>
                    {flightPlanDetail?.departureDelay &&
                      flightPlanDetail?.departureDelay !== "00:00:00" ? (
                      <p className="edit-flight-delay-text">
                        {getFlightDelayTime(flightPlanDetail?.departureDelay, flightPlanDetail?.departureTime)}
                      </p>
                    ) : null}
                    {isEditing ? (
                      <DateLocalizationProvider>
                        <div className="edit-flight-depart-arrival-container">
                          <div>Delay by</div>
                          <TimeField
                            id="departure-delay-time-picker"
                            className="edit-flight-timePicker"
                            format={TIME_FORMAT}
                            onChange={handleDelayTimeChange("departureDelay")}
                          />
                        </div>
                      </DateLocalizationProvider>
                    ) : null}
                  </div>
                </td>
                <td>
                  <div>
                    <p>
                      {`${dayjs(flightPlanDetail?.arrivalTime).format(
                        DATE_FORMAT
                      )} at ${dayjs(flightPlanDetail?.arrivalTime).format(
                        TIME_FORMAT
                      )}`}
                    </p>
                    {flightPlanDetail?.arrivalDelay &&
                      flightPlanDetail?.arrivalDelay !== "00:00:00" ? (
                      <p className="edit-flight-delay-text">
                        {getFlightDelayTime(flightPlanDetail?.arrivalDelay, flightPlanDetail?.arrivalTime)}
                      </p>
                    ) : null}
                    {isEditing ? (
                      <DateLocalizationProvider>
                        <div className="edit-flight-depart-arrival-container">
                          <div>Delay by</div>
                          <TimeField
                            id="arrival-delay-time-picker"
                            className="edit-flight-timePicker"
                            format={TIME_FORMAT}
                            onChange={handleDelayTimeChange("arrivalDelay")}
                          />
                        </div>
                      </DateLocalizationProvider>
                    ) : null}
                  </div>
                </td>

                <td>
                  <div>
                    <p>
                      {flightPlanDetail?.departureTimeFromArrivalAirport
                        ? `${dayjs(
                          flightPlanDetail?.departureTimeFromArrivalAirport
                        ).format(DATE_FORMAT)} at ${dayjs(
                          flightPlanDetail?.departureTimeFromArrivalAirport
                        ).format(TIME_FORMAT)}`
                        : "---"}
                    </p>
                    {flightPlanDetail?.departureDelayFromArrivalAirport &&
                      flightPlanDetail?.departureDelayFromArrivalAirport !==
                      "00:00:00" ? (
                      <p className="edit-flight-delay-text">
                        {getFlightDelayTime(
                          flightPlanDetail?.departureDelayFromArrivalAirport,
                          flightPlanDetail?.departureTimeFromArrivalAirport
                        )}
                      </p>
                    ) : null}
                    {isEditing ? (
                      <DateLocalizationProvider>
                        <div className="edit-flight-depart-arrival-container">
                          <div>Delay by</div>
                          <TimeField
                            id="departure-delay-from-arrival-airport-time-picker"
                            className="edit-flight-timePicker"
                            format={TIME_FORMAT}
                            onChange={handleDelayTimeChange(
                              "departureDelayFromArrivalAirport"
                            )}
                          />
                        </div>
                      </DateLocalizationProvider>
                    ) : null}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <SearchAircraftDialog
        open={showSearchAircraftDialog}
        onClose={() => setShowSearchAircraftDialog(false)}
        onSelectAircraft={handleAircraftOnSelect}
      />
    </DateLocalizationProvider>
  );
};

export default TripDetailsTable;
